import { pageSchemaScript, absUrl } from "@/seo/schema";
import { createFileRoute, Link } from "@tanstack/react-router";
import { SECTION_OG, sectionImageMeta } from "@/lib/ogSections";
import { PageShell, PageHero } from "@/components/PageShell";
import { Reveal } from "@/components/Reveal";
import { ArrowRight } from "lucide-react";
import { ASK_ARIF_ARTICLES, getCategory } from "@/lib/askArif";
import { AuthorCard } from "@/components/AuthorCard";

const ARTICLES = ASK_ARIF_ARTICLES.map((a) => ({
  slug: a.slug,
  category: getCategory(a.category)?.title ?? "Insights",
  title: a.question,
  excerpt: a.shortAnswer,
}));

const TITLE = "Articles & Guides | Arif Khan";
const DESCRIPTION = "Practical guides on CCTV, access control, fire alarm, home automation, perimeter security, auditorium AV, home cinema, and intelligent building planning.";

export const Route = createFileRoute("/insights/articles-guides")({
  component: ArticlesGuidesPage,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: "Practical guides across CCTV, access control, fire alarm, automation, AV, cinema and intelligent buildings." },
      { property: "og:url", content: "https://arifkhanglobal.com/insights/articles-guides" },
      ...sectionImageMeta(SECTION_OG.askArif, "Articles & guides — Arif Khan"),
    ],
    links: [
      { rel: "canonical", href: "https://arifkhanglobal.com/insights/articles-guides" },
    ],
    scripts: [
      pageSchemaScript({
        path: "/insights/articles-guides",
        type: "CollectionPage",
        name: TITLE,
        description: DESCRIPTION,
        breadcrumb: [{ name: "Insights", url: "/insights" }, { name: "Articles & Guides" }],
        extra: {
          mainEntity: {
            "@type": "ItemList",
            name: "Articles & Guides",
            numberOfItems: ARTICLES.length,
            itemListElement: ARTICLES.map((a, i) => ({
              "@type": "ListItem",
              position: i + 1,
              url: absUrl(`/ask-arif/${a.slug}`),
              name: a.title,
            })),
          },
        },
      }),
    ],
  }),
});

function ArticlesGuidesPage() {
  return (
    <PageShell>
      <PageHero
        eyebrow="Articles & Guides"
        title="Practical guides across security, automation, AV and intelligent buildings."
        description="Owner-facing and architect-facing guides on CCTV, access control, fire alarm, perimeter security, home automation, auditorium AV, home cinema and intelligent building planning."
      />
      <section className="py-24">
        <div className="container-page grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {ARTICLES.map((a, i) => (
            <Reveal key={a.slug} delay={i * 0.03}>
              <article className="rounded-2xl border border-border p-8 h-full bg-background hover:border-border-strong transition-colors flex flex-col">
                <span className="eyebrow text-accent">{a.category}</span>
                <h3 className="mt-5 text-xl font-semibold leading-snug tracking-tight">{a.title}</h3>
                <p className="mt-3 text-sm text-ink-soft leading-relaxed flex-1">{a.excerpt}</p>
                <Link to="/ask-arif/$slug" params={{ slug: a.slug }} className="mt-6 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-primary">
                  Read guide <ArrowRight className="h-3 w-3" />
                </Link>
              </article>
            </Reveal>
          ))}
        </div>
      </section>
      <section className="py-20 border-t border-border bg-surface">
        <div className="container-page max-w-4xl">
          <AuthorCard />
        </div>
      </section>
    </PageShell>
  );
}
